import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import { Loader } from "../../Common/Loader";
import { X, Loader as LoaderIcon } from "lucide-react";
import { Common } from "helpers/common";
import { ToastContainer } from "react-toastify";
import DeleteModal from "Common/DeleteModal";
import Drawer from "Common/Components/Drawer";
import { addClass, updateClass } from "helpers/backend_helper";
import { useAuth } from "hooks/useStore";
import { useClassStore } from "store/useClassStore";

type Props = { onHide: () => void; show: boolean, data: any; onSave: (result: any) => void };
export const AddClass: React.FC<Props> = ({ onHide, show, data, onSave }) => {

  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const { user } = useAuth();
  const { fetchClasses } = useClassStore();
  
  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const uiHelper = new Common();

  useEffect(() => {
    if (data?.classId) {
      setIsEdit(true);
      loadClass();
    } else {
      setIsEdit(false);
      setClassData({});
      validation.resetForm();
    }
  }, [data]);

  const loadClass = async () => {
    setIsLoading(true);
    try {
      setClassData(data);

      // Set form values
      validation.setValues({
        classId: data.classId,
        clientId: data.clientId || 1,
        className: data.className,
        description: data.description || ''
      });
    } catch (error) {
      console.error("Error loading class:", error);
      uiHelper.showErrorMessage("Error loading class data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawer = () => { onHide(); }

  // validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      classId: (data && data.classId) || 0,
      clientId: (data && data.clientId) || 1,
      className: (data && data.className) || "",
      description: (data && data.description) || ""
    },
    validationSchema: Yup.object({
      className: Yup.string().required("Please enter class name"),
      description: Yup.string()
    }),
    onSubmit: (values) => {
      handleSave(values);
    }
  });

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
console.log(`user`, user);

      const formData = {
        ...values,
        createdBy: user?.userId ?? 1,
        updatedBy: user?.userId ?? 1
      };

      let response;
      if (isEdit && values.classId) {
        // Update existing class
        response = await updateClass(formData);
        onSave(formData);
      } else {
        // Create new class
        response = await addClass(formData);
        onSave(response);
      }
      
      uiHelper.showSuccessMessage(
        `Class has been successfully ${isEdit ? 'updated' : 'saved'}.`
      );

      //onHide();
    } catch (error) {
      console.error("Error saving class:", error);
      uiHelper.showErrorMessage("Error saving class data");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <React.Fragment>
      <Drawer show={show} onHide={handleDrawer} id="classSidePenal" drawer-end="true" className="fixed inset-y-0 flex flex-col  
      transition-transform duration-600 ease-in-out transform bg-white shadow 
     dark:bg-zink-600 ltr:right-0 rtl:left-0  w-5/6 flex  z-drawer">

        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
          <div className="grow">
            <h5 className="mb-0 text-16">{isEdit ? "Edit" : "Add"} Class</h5>
          </div>
          <div className="shrink-0">
            <div className="flex items-center justify-end gap-[200px]">
              <div className="flex items-center gap-2 group">

              </div>
              <Drawer.Header data-drawer-close="classSidePenal" className="transition-all duration-150 ease-linear text-slate-500 hover:text-slate-800">
                <X className="size-4"></X>
              </Drawer.Header>
            </div>
          </div>
        </div>

        <Drawer.Body className="h-full p-0 overflow-y-auto">
          <div className="flex h-full">
            {/* Form Section - 100% width */}
            <div className="w-full h-full overflow-y-auto">
              <div className="h-full overflow-y-auto product-list">
                <div className="row">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        {isLoading ? (
                          <Loader />
                        ) : (
                          <form
                            id="classForm"
                            className="needs-validation"
                            onSubmit={(e) => {
                              e.preventDefault();
                              validation.handleSubmit();
                              return false;
                            }}
                          >
                            <div className="p-4">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                
                                {/* LEFT PANEL - Class Information */}
                                <div className="space-y-6">
                                  
                                  {/* Class Details Section */}
                                  <div className="bg-slate-50 dark:bg-zink-600 p-4 rounded-lg">
                                    <div className="mb-4">
                                      <h6 className="text-15 font-semibold text-slate-700 dark:text-zink-100 bg-white dark:bg-zink-700 px-3 py-2 rounded-md shadow-sm border border-slate-200 dark:border-zink-500">
                                        🏫 Class Information
                                      </h6>
                                    </div>
                                    
                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="className">
                                          Class Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          id="className"
                                          name="className"
                                          type="text"
                                          className={`form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${validation.touched.className && validation.errors.className ? "is-invalid" : ""}`}
                                          placeholder="Enter class name"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.className}
                                        />
                                        {validation.touched.className && validation.errors.className ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.className?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>

                                    <div className="space-y-4">
                                      <div>
                                        <label className="form-label" htmlFor="description">
                                          Description
                                        </label>
                                        <textarea
                                          id="description"
                                          name="description"
                                          className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                          placeholder="Enter description (optional)"
                                          onChange={validation.handleChange}
                                          onBlur={validation.handleBlur}
                                          value={validation.values.description}
                                          rows={4}
                                        />
                                        {validation.touched.description && validation.errors.description ? (
                                          <div className="mt-1 text-sm text-red-500">
                                            {validation.errors.description?.toString()}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* RIGHT PANEL - Additional Information */}
                                <div className="space-y-6">
                                  
                                  {/* Description Section */}
                                 
                                </div>
                              </div>
                            </div>
                          </form>
                        )}
                        <ToastContainer closeButton={false} limit={1} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer.Body>
        
        <Drawer.Footer className="flex items-center justify-end p-4 border-t border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-600">
          <div className="flex gap-2">
            <button
              type="button"
              className="text-slate-500 btn bg-white border-slate-200 hover:text-slate-600 hover:bg-slate-100 hover:border-slate-200 focus:text-slate-600 focus:bg-slate-100 focus:border-slate-200 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-100 active:border-slate-200 active:ring active:ring-slate-100 dark:bg-zink-700 dark:hover:bg-zink-600 dark:border-zink-500 dark:text-zink-200"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="classForm"
              className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <LoaderIcon className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                  Saving...
                </>
              ) : "Save"}
            </button>
          </div>
        </Drawer.Footer>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={() => {}}
        buttonCaption="Yes, Delete It!"
      />
    </React.Fragment>
  );
}

export default AddClass;

